import { useEffect, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { Lighting } from "./three/Lighting";
import { Decor } from "./three/Decor";
import { DecorDebugPanel, useTileGapNudge } from "./three/DecorDebugPanel";
import { CAMERA_HEIGHT, CAMERA_DISTANCE } from "./three/constants";

// Scène de test isolée pour ajuster l'écart entre tuiles de décor sans avoir
// à passer par toute la mécanique de jeu (admin → créer un set → lobby → jouer).
//
// Accès : http://localhost:8090/decor-test
//   - Caméra réglée identique à la spectator view (height 35, distance 35)
//   - Flèches ← / → (ou A / D) pour scroller le long de la piste
//   - Shift = scroll rapide
//   - Panneau debug en haut à droite : ajuste le nudge en live (persistance localStorage)

// Caméra latérale fixée à la même hauteur/recul que SideCamera en mode spectateur,
// translatable horizontalement via clavier pour scanner toute la piste.
function ScrollingSideCamera() {
  const { camera } = useThree();
  const targetX = useRef(0);
  const keys = useRef<Record<string, boolean>>({});

  useEffect(() => {
    const onDown = (e: KeyboardEvent) => { keys.current[e.key.toLowerCase()] = true; };
    const onUp = (e: KeyboardEvent) => { keys.current[e.key.toLowerCase()] = false; };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, []);

  useFrame((_, delta) => {
    const k = keys.current;
    const speed = (k["shift"] ? 80 : 25) * delta;
    if (k["arrowleft"] || k["a"] || k["q"]) targetX.current -= speed;
    if (k["arrowright"] || k["d"]) targetX.current += speed;
    if (k["home"]) targetX.current = 0;

    camera.position.set(targetX.current, CAMERA_HEIGHT, CAMERA_DISTANCE);
    camera.lookAt(new THREE.Vector3(targetX.current, 1, 0));
  });

  return null;
}

export function DecorTest() {
  const tileGapNudge = useTileGapNudge();
  const [tileLength, setTileLength] = useState<number | undefined>();

  return (
    <div className="absolute inset-0">
      <Canvas
        shadows
        camera={{ fov: 50, near: 0.1, far: 2000 }}
        style={{ background: "#87CEEB" }}
      >
        <ScrollingSideCamera />
        <Lighting />
        <Decor
          trackSlots={120}
          tileGapNudge={tileGapNudge}
          onTileLengthMeasured={setTileLength}
        />
        {/* Repères rouges au sol tous les 50 unités le long de l'axe X. */}
        {Array.from({ length: 21 }, (_, i) => i * 50).map((x) => (
          <mesh key={x} position={[x, 0.05, 0]}>
            <boxGeometry args={[0.4, 0.1, 6]} />
            <meshBasicMaterial color="red" />
          </mesh>
        ))}
      </Canvas>
      <DecorDebugPanel tileLengthHint={tileLength} />
      <div
        style={{
          position: "fixed",
          bottom: 12,
          left: 12,
          background: "rgba(0,0,0,0.72)",
          color: "#fff",
          padding: "8px 12px",
          borderRadius: 6,
          fontFamily: "ui-monospace, Menlo, monospace",
          fontSize: 12,
          lineHeight: 1.5,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4 }}>Decor Test Scene</div>
        <div>← / → ou A / D : scroller le long de la piste</div>
        <div>Shift : scroll rapide · Home : reset à 0</div>
        <div>Repères rouges tous les 50 u</div>
      </div>
    </div>
  );
}
